import { Fragment, useId, type ReactNode } from "react";

import { cn } from "../../lib/cn";
import { ChevronIcon } from "../icons";

export interface TableColumn<Row> {
	readonly key: string;
	readonly header: string;
	readonly align?: "left" | "right";
	/** Tertiary ink for secondary columns. */
	readonly muted?: boolean;
	readonly cell: (row: Row) => ReactNode;
}

export interface TableExpansion<Row> {
	/** The accessible name of a row's toggle, built from the row: "Values of Updated company identity, Sep 13". */
	readonly toggleLabel: (row: Row) => string;
	/** Whether a row's panel is open. */
	readonly isOpen: (row: Row) => boolean;
	/** Opens or closes a row's panel. */
	readonly onToggle: (row: Row) => void;
	/** What an open row shows beneath it, across every column. A row this returns null for gets no toggle. */
	readonly panel: (row: Row) => ReactNode;
}

export interface TableProps<Row> {
	/** Accessible name of the table, usually the card title. */
	readonly label: string;
	readonly columns: readonly TableColumn<Row>[];
	readonly rows: readonly Row[];
	readonly rowKey: (row: Row) => string | number;
	/** Tint rows on hover; use it when a cell holds the row's link. */
	readonly interactiveRows?: boolean;
	/** Lets each row unfold a panel of its own beneath it, behind a chevron in a leading column. */
	readonly expansion?: TableExpansion<Row>;
	/**
	 * Sets the rows under headings: each run of consecutive rows this names alike — the day of each entry, say — is a
	 * group of its own under a heading row that spans every column. The rows must already be in the order of the
	 * groups.
	 */
	readonly groupBy?: (row: Row) => string;
	/**
	 * Fits the table to the card it sits in. Without it a table keeps 560px and scrolls sideways when the card is
	 * narrower, which suits a wide table; a table of two short columns in a narrow card would scroll its figures out of
	 * sight instead.
	 */
	readonly fit?: boolean;
}

/** The rows in runs `groupBy` names alike, in order; one run with no heading when there is no `groupBy`. */
function groupsOf<Row>(
	rows: readonly Row[],
	groupBy: ((row: Row) => string) | undefined,
): { heading: string | undefined; rows: Row[] }[] {
	if (groupBy === undefined) {
		return [{ heading: undefined, rows: [...rows] }];
	}
	const groups: { heading: string; rows: Row[] }[] = [];
	for (const row of rows) {
		const heading = groupBy(row);
		const last = groups.at(-1);
		if (last?.heading === heading) {
			last.rows.push(row);
		} else {
			groups.push({ heading, rows: [row] });
		}
	}
	return groups;
}

/** Hairline table: 10.5px uppercase head, 12.5px rows, no zebra striping. */
export function Table<Row>({
	label,
	columns,
	rows,
	rowKey,
	interactiveRows = false,
	expansion,
	groupBy,
	fit = false,
}: TableProps<Row>) {
	const baseId = useId();
	return (
		<div className="thin-scroll overflow-x-auto">
			<table aria-label={label} className={cn("w-full border-collapse", !fit && "min-w-[560px]")}>
				<thead>
					<tr>
						{expansion === undefined ? null : (
							<th scope="col" className="w-[38px] border-b border-line px-4 py-[9px]">
								<span className="sr-only">Values</span>
							</th>
						)}
						{columns.map(column => (
							<th
								key={column.key}
								scope="col"
								className={cn(
									"border-b border-line px-4 py-[9px] text-left text-label font-semibold tracking-caps whitespace-nowrap text-ink-3 uppercase",
									column.align === "right" && "text-right",
								)}
							>
								{column.header}
							</th>
						))}
					</tr>
				</thead>
				{groupsOf(rows, groupBy).map(group => (
					<tbody key={group.heading ?? "rows"}>
						{group.heading === undefined ? null : (
							<tr>
								<th
									scope="rowgroup"
									colSpan={columns.length + (expansion === undefined ? 0 : 1)}
									className="border-y border-line bg-surface-2 px-4 py-[7px] text-left text-label font-semibold tracking-caps text-ink-3 uppercase"
								>
									{group.heading}
								</th>
							</tr>
						)}
						{group.rows.map(row => {
							const panel = expansion?.panel(row) ?? null;
							const open = expansion !== undefined && panel !== null && expansion.isOpen(row);
							const panelId = `${baseId}-${String(rowKey(row))}`;
							return (
								<Fragment key={rowKey(row)}>
									<tr
										className={cn(
											"border-b border-line transition-colors duration-100 last:border-0",
											interactiveRows && "hover:bg-hover-tint",
										)}
									>
										{expansion === undefined ? null : (
											<td className="px-4 py-[11px] align-middle">
												{panel === null ? null : (
													<button
														type="button"
														aria-label={expansion.toggleLabel(row)}
														aria-expanded={open}
														aria-controls={open ? panelId : undefined}
														onClick={() => {
															expansion.onToggle(row);
														}}
														className="flex h-6 w-6 press cursor-pointer items-center justify-center rounded-ctl border border-line bg-transparent text-ink-3 hover:bg-bg"
													>
														<ChevronIcon
															className={cn(
																"h-[11px] w-[11px] fill-none stroke-current stroke-[1.8] transition-transform duration-100 motion-reduce:transition-none",
																open && "rotate-90",
															)}
															strokeLinecap="round"
															strokeLinejoin="round"
														/>
													</button>
												)}
											</td>
										)}
										{columns.map(column => (
											<td
												key={column.key}
												className={cn(
													"px-4 py-[11px] align-middle text-ctl-lg text-ink",
													column.align === "right" && "text-right tabular-nums",
													column.muted && "text-ink-3",
												)}
											>
												{column.cell(row)}
											</td>
										))}
									</tr>
									{open ? (
										<tr className="border-b border-line last:border-0">
											<td id={panelId} colSpan={columns.length + 1} className="px-4 pt-0 pb-3">
												{panel}
											</td>
										</tr>
									) : null}
								</Fragment>
							);
						})}
					</tbody>
				))}
			</table>
		</div>
	);
}
